// @flow
import React, { useEffect, useMemo, useReducer, useCallback, type Node, type Context } from 'react';
import { useDispatch } from 'react-redux';
// import singletonAsync from 'app/common/lib/singletonAsync';
import map from 'ramda/es/map';
import omit from 'ramda/es/omit';
import clone from 'ramda/es/clone';
import forEachObjIndexed from 'ramda/es/forEachObjIndexed';

import action from 'app/common/lib/action';
import api /* { apiCancelable } */ from 'app/actions/api';

import standartRequests from './standartRequests';
import reducer from './reducer';

/**
 * DEPRECATED
 * Api - компонент для получения данных из api
 * с последующим помещением их в context React'а
 * ------------------------
 */

const emptyObj = {};

type RestType = {
  ['params' | 'data']: Object,
}

export type OccurType = {
  [id: string]: boolean,
}

export type ErrorByIdType = {
  [id: string]: {
    err: string,
    path: string,
    rest?: Object,
    error: typeof Error,
  },
}

type StandartRequestFuncParamsType = {
  path: string,
  rest?: RestType,
  id?: string, // можно добавить id, по которому будет изменятся процесс выполнения запроса в occur
  cbErrorTextCreate: (error?: typeof Error) => string, // функция для создания текста ошибки
  fnContentUpdate?: (resData: any, state: Object) => Object, // преобразование данных после запроса
}

export type StandartRequestFuncType = (p: StandartRequestFuncParamsType) => Promise<boolean>

export type RequestFuncType = (void) => Promise<boolean>
export type UpdateFn = (content: Object) => Object

// const apiRequest = singletonAsync(apiCancelable.requests); // не работает try ... catch
// const apiRequest = apiCancelable.request;

const initialState = {
  content: {}, // данные, которые возвращают запросы
  loading: true, // процесс выполнения запроса
  occur: {}, // процесс выполнения для запросов имеющих id
  error: null,
};

type ApiPropsType = {
  apiData: Context<Object>,
  fnApi?: (url: string, method: string, rest: Object) => Promise<any>,
  children: Node | (contextValue: Object) => Node,
  descriptions?: {
    [req: string]: {
      path: string,
      type: $Values<typeof standartRequests>,
      rest?: RestType,
      cbErrorTextCreate: (error: typeof Error) => string,
      autoRequest: boolean,
      id?: string,
      fnContentUpdate?: (resData: any, content: any) => any,
    }
  },
  addStandartRequests?: boolean,
  initialContent?: any,
  consoleLogName?: string,
}

type DoitFunctionType = {
  path: string,
  type: $Values<typeof standartRequests>,
  rest: Object,
  cbErrorTextCreate: (error?: typeof Error) => string,
  id?: string,
  fnContentUpdate?: (resData: any, state: Object) => Object,
}

const Api = ({
  apiData: ApiData,
  fnApi = api,
  children,
  descriptions = emptyObj,
  addStandartRequests,
  initialContent = emptyObj,
  consoleLogName,
}: ApiPropsType) => {
  // dispatch из глобального состояния чтоб отправить ошибку
  const dispatch = useDispatch();

  // локальное состояние для результата запроса, он так же будет доступен в контексте у всех детей
  const defaultState = useMemo(() => ({ ...initialState, content: initialContent }), [initialContent]); // ??? нужно ли
  // $FlowFixMe
  const [state, d] = useReducer(reducer(defaultState), defaultState);

  if (consoleLogName) console.log(`${consoleLogName}: `, state); // eslint-disable-line no-console

  // функция выполняющая запрос
  // TODO нужно что-то придумать для прерываемых запросов,
  const doit = useCallback(async ({
    path,
    type,
    rest,
    cbErrorTextCreate,
    id,
    fnContentUpdate,
  }: DoitFunctionType) => {
    try {
      d({ type: id ? 'requests_id' : 'requests', payload: null, id });
      const res = await fnApi(path, type, rest);
      const data = fnContentUpdate ? fnContentUpdate(res.data, state.content) : res.data;
      d({ type: id ? 'success_id' : 'success', payload: omit(['result'], data), id });
      return true;
    } catch (error) {
      const errorSave = {
        err: cbErrorTextCreate(error),
        path,
        rest,
        error: clone(error),
      };
      d({ type: id ? 'failure_id' : 'failure', payload: errorSave, id });
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // сборка запросов: стандартные из набора standartRequests и запросы по переданному
  // параметру 'descriptions'. Запросы можно выполнять получив соответствующие функции
  // из контекста. Стнадартные (включаются если передать addStandartRequests: true):
  // contextData.request.post({
  //     path: 'example/path',
  //     rest: { data: { some_id: 323 } },
  //     cbErrorTextCreate: (error) => ('Текст ошибки'),
  //     id: 'saving',
  //   })
  // Запросы из описания:
  // contextData.request.keyFromDescription();
  // Вторые могут быть автозапускаемыми, при наличии autoRequest === true в описании
  const requests = useMemo(() => ({
    ...(addStandartRequests ? map(type => ({ path, rest, cbErrorTextCreate, id, fnContentUpdate }) => doit({ path, type, rest, cbErrorTextCreate, id, fnContentUpdate }), standartRequests) : {}),
    ...map(({ path, type, rest, cbErrorTextCreate, id, fnContentUpdate }) => () => doit({ path, type, rest, cbErrorTextCreate, id, fnContentUpdate }), descriptions),
  }), [descriptions, doit, addStandartRequests]);

  const updateFn = useCallback((fn) => {
    d({ type: 'update', payload: fn, id: '' });
  }, []);

  // весь контекст: состояние + объект с запросами
  const contextValue = useMemo(() => ({
    ...state,
    request: requests,
    updateFn,
  }), [state, requests, updateFn]);

  // опрос данных на случай autoRequest === true, в этом случае запрос выполняется
  useEffect(() => {
    forEachObjIndexed(({ autoRequest }, key) => autoRequest && requests[key](), descriptions);
  }, [descriptions, requests]);

  return (
    <ApiData.Provider value={contextValue}>
      {(typeof children === 'function')
        // если children функция, то в нее передаются все данные
        ? children(contextValue)
        : children}
    </ApiData.Provider>
  );
};

export default Api;
